import { animate, AnimationTriggerMetadata, state, style, transition, trigger } from '@angular/animations';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';

import { BlockNodeEnum } from '@core/enums';
import { Block } from '@core/models/block/block.model';
import { GlobalService, MediaQuery, MediaQueryState } from '@core/services/common/global.service';
import { BlockComponent } from '@modules/creator/components/block/block.component';
import { anyParentHasTabIndex } from '@shared/helpers/dom-helpers';

import { HoverEvent, SubmenuService } from '@shared/services/submenu.service';

import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { MainMenuService } from '../main-menu/services/main-menu.service';

export const submenuAnimations: {
  readonly submenuContainer: AnimationTriggerMetadata;
} = {
  /** Animation that is applied on the dialog container by default. */
  submenuContainer: trigger('submenuContainer', [
    // Note: The `enter` animation transitions to `transform: none`, because for some reason
    // specifying the transform explicitly, causes IE both to blur the dialog content and
    // decimate the animation performance. Leaving it as `none` solves both issues.
    state('void, exit', style({opacity: 0})),
    transition(
      '* => enter',
      animate('150ms cubic-bezier(0, 0, 0.2, 1)', style({opacity: 1})),
    ),
    transition(
      '* => void, * => exit',
      animate('75ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({opacity: 0})),
    ),
  ]),
};

const hasAnchorSubmenu = (item: Block) =>
  item?.node === BlockNodeEnum.ANCHOR_NODE &&
  item?.children.length > 0 &&
  item?.children[0]?.children.length > 0;

const hasParagraphSubmenu = (item: Block) =>
  item?.node === BlockNodeEnum.PARAGRAPH_NODE &&
  !!item?.hrefUrl &&
  item?.children.length > 0;

const hasSubmenu = (item: Block) =>
  hasAnchorSubmenu(item) || hasParagraphSubmenu(item);

@Component({
  selector: '[app-link]',
  templateUrl: './link-block.component.html',
  styleUrls: ['./link-block.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [submenuAnimations.submenuContainer],
  host: {
    '(click)': 'onClick($event)',
  },
  standalone: false,
})
export class LinkBlockComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() item: Block;
  @Input() classname: string;
  linkAddedToParent = false;
  tabindexAddedToParent = false;
  @ViewChild('submenuContainerRef', {read: ViewContainerRef})
  submenuContainerRef: ViewContainerRef;
  @ViewChild('submenuPanel', {read: ElementRef}) submenuPanel: ElementRef;
  disabled: boolean;
  state: 'void' | 'enter' | 'exit' = 'exit';
  private _mouseleave: Function;
  private _mouseover: Function;
  private _mediaQuerySubscription = Subscription.EMPTY;

  constructor(
    private _renderer: Renderer2,
    private globalService: GlobalService,
    private submenuService: SubmenuService,
    private mainMenuService: MainMenuService,
    private element: ElementRef,
  ) {
  }

  get hesSubmenu(): boolean {
    return hasSubmenu(this.item);
  }

  onClick(event: MouseEvent) {
    if (this.disabled) {
      const target = event.target as any;
      this.submenuService.menuToggle.next({
        mouseEvent: 'mouseover',
        target: target,
        menuRect: target.getBoundingClientRect(),
      });
      event.stopPropagation();
    }
    this.disabled = false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.item) {
      this.submenuService.hasSubmenu = hasSubmenu(this.item);
    }
  }

  ngAfterViewInit(): void {
    if (this.submenuService.hasSubmenu) {
      this.createSubmenuPanel();
      this.subscribeForMouseEvent();
      this.updateMenuOrientationSettings();
      this._mediaQuerySubscription = this.globalService.mediaQuery
        .pipe(distinctUntilChanged())
        .subscribe((mq: MediaQuery) => this.switchSubmenu(mq));
    }
    const anc = this.element.nativeElement.closest('a');
    if (anc) {
      this.linkAddedToParent = true;
    }
    if (this.item.tabindex > -1) {
      this.tabindexAddedToParent = anyParentHasTabIndex(
        this.element.nativeElement,
      );
    }
    if (this.item.link.getLink()) {
      this.element.nativeElement.setAttribute('valid-link', true);
    }
  }

  ngOnDestroy(): void {
    this.deregisterMouseOverAndLeavePanelCallback();
    this._mediaQuerySubscription.unsubscribe();
  }

  private updateMenuOrientationSettings() {
    const settings = this.mainMenuService.settings.getValue();
    if (settings) {
      this._renderer.addClass(
        this.submenuPanel.nativeElement,
        settings.orientation,
      );
    }
  }

  private onMouseEvent(hoverEvent: HoverEvent) {
    const submenuActive = this.submenuService.subMenuPanelActive.value;
    if (hoverEvent.mouseEvent === 'mouseover') {
      this.registerMouseLeavePanelCallback();
      this.initializeSubmenuRect(hoverEvent.menuRect);
      this.isOverTheNavigationItem();
    }

    if (hoverEvent.mouseEvent === 'mouseleave' && !submenuActive) {
      this.deregisterMouseOverAndLeavePanelCallback();
      this.isLeaveTheNavigationItem();
    }
  }

  private createSubmenuPanel() {
    const component = this.submenuContainerRef.createComponent(BlockComponent);
    component.instance.item = this.item.children[0];
  }

  private isOverTheNavigationItem() {
    this.state = 'enter';
    this._renderer.removeClass(this.submenuPanel.nativeElement, 'hide');
    this._renderer.addClass(this.submenuPanel.nativeElement, 'show');
  }

  private isLeaveTheNavigationItem() {
    this.state = 'exit';
    this._renderer.removeClass(this.submenuPanel.nativeElement, 'show');
    this._renderer.addClass(this.submenuPanel.nativeElement, 'hide');
  }

  private registerMouseLeavePanelCallback() {
    this._mouseleave = this._renderer.listen(
      this.submenuPanel.nativeElement,
      'mouseleave',
      (args) => {
        const targetElement = args.toElement ?? args.currentTarget;
        if (this.isTheSameTargetElement(targetElement)) {
          this.isLeaveTheNavigationItem();
          this._mouseleave();
        }
        this.submenuService.subMenuActive(false);
      },
    );

    this._mouseover = this._renderer.listen(
      this.submenuPanel.nativeElement,
      'mouseover',
      () => this.submenuService.subMenuActive(true),
    );
  }

  private isTheSameTargetElement(arg: any) {
    return this.submenuService.menuToggle.value.target !== arg.parentElement;
  }

  private initializeSubmenuRect(menuRect: any) {
    const settings = this.mainMenuService.settings.getValue();
    if (settings?.orientation === 'vertical') {
      return;
    }

    const {height} = menuRect;
    this._renderer.setStyle(
      this.submenuPanel.nativeElement,
      'margin-top',
      `${height}px`,
    );
  }

  private deregisterMouseOverAndLeavePanelCallback() {
    if (this._mouseleave) {
      this._mouseleave();
    }

    if (this._mouseover) {
      this._mouseover();
    }
  }

  private subscribeForMouseEvent() {
    this.submenuService.menuToggle.pipe(debounceTime(50)).subscribe({
      next: (p) => this.onMouseEvent(p),
    });
  }

  private switchSubmenu(mq: MediaQuery) {
    if (!this.submenuPanel) {
      return;
    }

    const isDesktopMode = mq.mediaQueryState === MediaQueryState.Desktop;
    if (!isDesktopMode) {
      this._renderer.addClass(this.submenuPanel.nativeElement, 'mobile');
    } else {
      this._renderer.removeClass(this.submenuPanel.nativeElement, 'mobile');
    }

    this.disabled = !isDesktopMode;
  }
}
